/* wallashop artistic interactions — 5 effects, vanilla JS, no libs.
 * cursor_graphite_trail · scroll_progress · magnetic_cursor ·
 * typography_reveal · mobile_scroll_blobs. Respects prefers-reduced-motion.
 * Debug API: window.wallashopDebug.enable(name) / .disable(name). */
(function () {
  'use strict';

  var REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var CAN_HOVER = matchMedia('(hover: hover) and (pointer: fine)').matches;
  var ROOT = document.getElementById('ws-root');
  if (!ROOT) return;

  var PALETTE = ['#0A0A0A', '#1B2845', '#B8893E', '#5C6E3F'];
  var modules = Object.create(null);

  function register(name, setup) {
    modules[name] = { setup: setup, teardown: null, active: false };
  }
  function enable(name) {
    var m = modules[name];
    if (!m || m.active) return;
    var t = m.setup();
    if (t) { m.teardown = t; m.active = true; }
  }
  function disable(name) {
    var m = modules[name];
    if (!m || !m.active) return;
    try { m.teardown && m.teardown(); } catch (e) {}
    m.teardown = null; m.active = false;
  }

  function hexToRgba(hex, a) {
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
  }

  // --- A) CURSOR GRAPHITE TRAIL — real path-based HB pencil line anchored to
  // the page. No stamp images: every pointer segment is stroked directly with
  // three passes (one main + two jittered at ±0.8–1.5 px perpendicular) to
  // produce the split-grain look of a soft pencil where bristles miss the
  // previous stroke. Tiny square "grain dots" (0.4–1.0 px, ≤6 per segment)
  // scatter perpendicular to the stroke to simulate graphite catching on
  // paper tooth. Line width varies 1.0–1.4 px per frame; alpha rides cursor
  // speed (slow = more pressure, darker; fast = lighter). Canvas is a
  // position:absolute child of <body> sized to document height so strokes
  // stay glued to page coordinates (pageX, pageY) across scroll. Height is
  // capped by a 48 MB RAM budget and 15000 px. Fade uses destination-out
  // over the visible viewport ± 500 px — cheap on long canvas and immune to
  // the premultiplied-alpha residue that source-over fade suffers. mix-blend-
  // mode multiply darkens underlying page content like real graphite. ---
  var GRAPHITE_COLOR = '#2a2a2a';
  var MAX_CANVAS_HEIGHT = 15000;
  var CANVAS_RAM_BUDGET = 48 * 1024 * 1024; // 48 MB safety margin under 50

  register('cursor_graphite_trail', function () {
    if (REDUCED || !CAN_HOVER) return null;

    var canvas = document.createElement('canvas');
    canvas.id = 'ws-graphite-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText =
      'position:absolute;top:0;left:0;' +
      'pointer-events:none;z-index:9;' +
      'mix-blend-mode:multiply;display:block';
    document.body.appendChild(canvas);
    var ctx = canvas.getContext('2d', { alpha: true });

    var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    var w = 0, h = 0;
    var mx = -9999, my = -9999, pmx = -9999, pmy = -9999;
    var isHover = false, rafId = null, hasInput = false;
    var resizeTimer = null, mutTimer = null, mutObs = null;
    var stats = { segments: 0, fps: 0, _f: 0, _t: performance.now(),
                  width: 0, height: 0, bytes: 0 };

    function computeHeight(widthCss) {
      var byMem = Math.floor(CANVAS_RAM_BUDGET / (widthCss * dpr * dpr * 4));
      return Math.min(document.body.scrollHeight, byMem, MAX_CANVAS_HEIGHT);
    }

    function resize() {
      w = window.innerWidth;
      h = computeHeight(w);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = GRAPHITE_COLOR;
      ctx.fillStyle = GRAPHITE_COLOR;
      stats.width = w; stats.height = h;
      stats.bytes = w * dpr * h * dpr * 4;
    }
    function scheduleResize() {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, 200);
    }
    function scheduleMutation() {
      if (mutTimer) clearTimeout(mutTimer);
      mutTimer = setTimeout(function () {
        var newH = computeHeight(w);
        if (Math.abs(newH - h) >= 500) resize();
      }, 300);
    }

    function onOver(e) {
      var t = e.target;
      isHover = !!(t && t.closest && t.closest('a, button, [role="button"], .tile-product, .tile-link'));
    }
    function onMove(e) {
      mx = e.clientX;
      my = e.clientY + window.scrollY;
      hasInput = true;
    }

    // Draw one pencil segment: main stroke + 2 jittered passes + grain dots.
    // speed is px/frame; slower → higher pressure (darker), faster → lighter.
    function drawSegment(x0, y0, x1, y1, speed) {
      if (y0 < 0 && y1 < 0) return;
      if (y0 > h && y1 > h) return;
      var dx = x1 - x0, dy = y1 - y0;
      var len = Math.sqrt(dx * dx + dy * dy);
      if (len < 0.5) return;
      var nx = -dy / len, ny = dx / len; // unit perpendicular

      var pressure = Math.max(0.14, 0.38 - speed * 0.004);
      var boost = isHover ? 1.25 : 1.0;
      var baseAlpha = pressure * boost;

      // MAIN pass — sharpest, central
      ctx.globalAlpha = baseAlpha;
      ctx.lineWidth = 1.0 + Math.random() * 0.4;
      ctx.beginPath();
      ctx.moveTo(x0, y0);
      ctx.lineTo(x1, y1);
      ctx.stroke();

      // TWO jittered passes — softer, offset perpendicular
      ctx.globalAlpha = baseAlpha * 0.45;
      for (var p = 0; p < 2; p++) {
        ctx.lineWidth = 0.5 + Math.random() * 0.4;
        var off = (Math.random() - 0.5) * 2.8;
        ctx.beginPath();
        ctx.moveTo(x0 + nx * off, y0 + ny * off);
        ctx.lineTo(x1 + nx * off, y1 + ny * off);
        ctx.stroke();
      }

      // PAPER GRAIN — scatter tiny square dots along the segment
      ctx.globalAlpha = baseAlpha * 0.55;
      var grains = Math.min(6, Math.floor(len * 0.75) + 1);
      for (var g = 0; g < grains; g++) {
        var t = Math.random();
        var gx = x0 + dx * t + nx * (Math.random() - 0.5) * 3.2;
        var gy = y0 + dy * t + ny * (Math.random() - 0.5) * 3.2;
        var r = 0.4 + Math.random() * 0.6;
        ctx.fillRect(gx - r / 2, gy - r / 2, r, r);
      }

      ctx.globalAlpha = 1;
      stats.segments++;
    }

    function draw() {
      var now = performance.now();
      stats._f++;
      if (now - stats._t > 1000) {
        stats.fps = Math.round(stats._f * 1000 / (now - stats._t));
        stats._f = 0; stats._t = now;
      }

      // Fade visible viewport ± 500 px buffer using destination-out (scales r
      // and a together so strokes fade cleanly to transparent, revealing the
      // page beneath via mix-blend-mode). Source-over fade leaves premul
      // residue.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.globalAlpha = 0.07;
      ctx.fillStyle = '#000';
      var scrollY = window.scrollY;
      var yStart = Math.max(0, scrollY - 500);
      var yHeight = Math.min(h - yStart, window.innerHeight + 1000);
      if (yHeight > 0) ctx.fillRect(0, yStart, w, yHeight);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.fillStyle = GRAPHITE_COLOR;

      if (hasInput && pmx !== -9999 && (mx !== pmx || my !== pmy)) {
        var dx = mx - pmx, dy = my - pmy;
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0) {
          // Break long jumps (fast moves) into shorter sub-segments so the
          // grain pattern stays dense instead of spread thin.
          var step = isHover ? 3.5 : 6.0;
          var subs = Math.max(1, Math.floor(dist / step));
          for (var i = 1; i <= subs; i++) {
            var ta = (i - 1) / subs, tb = i / subs;
            drawSegment(pmx + dx * ta, pmy + dy * ta,
                        pmx + dx * tb, pmy + dy * tb,
                        dist);
          }
        }
      }
      if (mx !== -9999) { pmx = mx; pmy = my; }
      rafId = requestAnimationFrame(draw);
    }
    function onVis() {
      if (document.hidden && rafId) { cancelAnimationFrame(rafId); rafId = null; }
      else if (!document.hidden && !rafId) {
        rafId = requestAnimationFrame(draw);
      }
    }

    resize();
    window.addEventListener('resize', scheduleResize);
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('mouseover', onOver, { passive: true });
    document.addEventListener('visibilitychange', onVis);
    mutObs = new MutationObserver(scheduleMutation);
    mutObs.observe(document.body, { childList: true, subtree: true });
    rafId = requestAnimationFrame(draw);
    if (window.wallashopDebug) window.wallashopDebug.graphiteStats = stats;

    return function () {
      if (rafId) cancelAnimationFrame(rafId);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (mutTimer) clearTimeout(mutTimer);
      if (mutObs) { mutObs.disconnect(); mutObs = null; }
      window.removeEventListener('resize', scheduleResize);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      document.removeEventListener('visibilitychange', onVis);
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      if (window.wallashopDebug) delete window.wallashopDebug.graphiteStats;
    };
  });

  // --- B) SCROLL PROGRESS BAR — scaleX transform, monospace readout ---
  register('scroll_progress', function () {
    var wrap = document.getElementById('ws-scroll-progress');
    if (!wrap) return null;
    var bar = document.getElementById('ws-scroll-bar');
    var pct = document.getElementById('ws-scroll-pct');
    var ticking = false;

    function update() {
      var doc = document.documentElement;
      var max = (doc.scrollHeight - doc.clientHeight) || 1;
      var p = Math.max(0, Math.min(1, (window.scrollY || doc.scrollTop) / max));
      bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      if (pct) pct.textContent = String(Math.round(p * 100)).padStart(3, '0') + '%';
      ticking = false;
    }
    function onScroll() { if (!ticking) { requestAnimationFrame(update); ticking = true; } }

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    wrap.style.display = 'block';

    return function () {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      wrap.style.display = 'none';
    };
  });

  // --- C) MAGNETIC CURSOR — header nav + product tiles only.
  // Restricted to signature interactions to preserve precision on functional
  // navigation like sidebar filters. ---
  register('magnetic_cursor', function () {
    if (REDUCED || !CAN_HOVER) return null;
    var RADIUS = 60, MAX_SHIFT = 4;
    var targets = [];

    function collect() {
      targets.length = 0;
      var list = document.querySelectorAll('header a, .tile-product, .tile-link');
      for (var i = 0; i < list.length; i++) targets.push(list[i]);
    }
    function reset(el) { el.style.transform = ''; el.style.willChange = ''; }

    // Closest-point-to-rect distance; below RADIUS, pull element toward cursor.
    function onMove(e) {
      var cx = e.clientX, cy = e.clientY;
      for (var i = 0; i < targets.length; i++) {
        var el = targets[i];
        var r = el.getBoundingClientRect();
        var nx = Math.max(r.left, Math.min(cx, r.right));
        var ny = Math.max(r.top, Math.min(cy, r.bottom));
        var dx = cx - nx, dy = cy - ny;
        var d = Math.sqrt(dx * dx + dy * dy);
        if (d < RADIUS) {
          var force = 1 - d / RADIUS;
          var tx = (cx - (r.left + r.width / 2)) * force * 0.08;
          var ty = (cy - (r.top + r.height / 2)) * force * 0.08;
          tx = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, tx));
          ty = Math.max(-MAX_SHIFT, Math.min(MAX_SHIFT, ty));
          el.style.willChange = 'transform';
          el.style.transform = 'translate3d(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px,0)';
        } else if (el.style.transform) {
          reset(el);
        }
      }
    }

    collect();
    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('shopify:section:load', collect);

    return function () {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('shopify:section:load', collect);
      for (var i = 0; i < targets.length; i++) reset(targets[i]);
      targets.length = 0;
    };
  });

  // --- D) TYPOGRAPHY REVEAL — split headlines, stagger 25ms ---
  register('typography_reveal', function () {
    if (REDUCED || !('IntersectionObserver' in window)) return null;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { reveal(en.target); obs.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });

    // Skip headings with nested markup (links/images/badges) to avoid breaking them.
    function isPlain(el) {
      for (var i = 0; i < el.childNodes.length; i++) {
        if (el.childNodes[i].nodeType !== 3) return false;
      }
      return !!el.textContent.trim();
    }

    var LETTER_CSS = 'display:inline-block;transform:translateY(0.4em);opacity:0;will-change:transform,opacity';

    function split(el) {
      var words = el.textContent.split(/(\s+)/), letters = [], i, j, w, wrap, s;
      el.textContent = '';
      for (i = 0; i < words.length; i++) {
        w = words[i];
        if (/^\s+$/.test(w)) { el.appendChild(document.createTextNode(w)); continue; }
        wrap = document.createElement('span');
        wrap.style.cssText = 'display:inline-block;white-space:nowrap';
        for (j = 0; j < w.length; j++) {
          s = document.createElement('span');
          s.textContent = w.charAt(j);
          s.style.cssText = LETTER_CSS;
          wrap.appendChild(s);
          letters.push(s);
        }
        el.appendChild(wrap);
      }
      return letters;
    }

    function reveal(el) {
      el.setAttribute('data-ws-done', '1');
      if (!isPlain(el)) return;
      split(el).forEach(function (s, i) {
        var delay = i * 25;
        s.style.transition = 'transform 480ms cubic-bezier(.2,.7,.2,1) ' + delay + 'ms, opacity 480ms ease ' + delay + 'ms';
        requestAnimationFrame(function () { s.style.transform = 'translateY(0)'; s.style.opacity = '1'; });
        setTimeout(function () { s.style.willChange = ''; }, 520 + delay);
      });
    }

    function scan() {
      var list = document.querySelectorAll('h1:not([data-ws-done]), h2:not([data-ws-done])');
      for (var i = 0; i < list.length; i++) obs.observe(list[i]);
    }

    scan();
    document.addEventListener('shopify:section:load', scan);

    return function () {
      obs.disconnect();
      document.removeEventListener('shopify:section:load', scan);
    };
  });

  // --- E) MOBILE SCROLL BLOBS + TAP RIPPLE — real touch only, soft circles.
  // Uses div+border-radius+blur for a diffused overlay. Disabled by default
  // (settings_data.json) so it only runs when a merchant opts in. Gate uses
  // '(hover:none) and (pointer:coarse)' to exclude desktop devtools emulation. ---
  register('mobile_scroll_blobs', function () {
    if (REDUCED) return null;
    if (!matchMedia('(hover: none) and (pointer: coarse)').matches) return null;
    var container = document.getElementById('ws-blob-container');
    if (!container) return null;
    var MAX_ACTIVE = 3;
    var active = 0;
    var lastY = window.scrollY;
    var lastT = Date.now();
    var lastSpawnT = 0;
    var scrollRaf = null;

    function spawnBlob(side) {
      if (active >= MAX_ACTIVE) return;
      var size = 180 + Math.random() * 140;
      var color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
      var blob = document.createElement('div');
      blob.className = 'ws-blob';
      blob.style.cssText =
        'width:' + size + 'px;height:' + size + 'px;' +
        'top:' + (10 + Math.random() * 60) + 'vh;' +
        (side === 'left' ? 'left:-10vw;' : 'right:-10vw;') +
        'background:' + hexToRgba(color, 0.15) + ';' +
        'border-radius:50%;opacity:0;' +
        'transition:transform 1200ms cubic-bezier(.22,.61,.36,1),opacity 400ms ease;';
      container.appendChild(blob);
      active++;
      requestAnimationFrame(function () {
        blob.style.transform = 'translate3d(' + (side === 'left' ? '30vw' : '-30vw') + ',0,0)';
        blob.style.opacity = '1';
      });
      setTimeout(function () {
        blob.style.transition = 'opacity 800ms ease';
        blob.style.opacity = '0';
      }, 800);
      setTimeout(function () {
        if (blob.parentNode) blob.parentNode.removeChild(blob);
        active--;
      }, 1600);
    }

    function spawnFromScroll(velocity) {
      var now = Date.now();
      if (now - lastSpawnT < 100) return;
      var speed = Math.abs(velocity);
      if (speed < 0.4) return;
      lastSpawnT = now;
      var slots = MAX_ACTIVE - active;
      if (slots <= 0) return;
      var count = Math.min(slots, Math.max(1, Math.floor(speed / 1.8)));
      for (var i = 0; i < count; i++) {
        spawnBlob(Math.random() < 0.5 ? 'left' : 'right');
      }
    }

    function measure() {
      var y = window.scrollY, t = Date.now(), dt = Math.max(1, t - lastT);
      var velocity = (y - lastY) / dt;
      lastY = y; lastT = t;
      spawnFromScroll(velocity);
      scrollRaf = null;
    }
    function onScroll() { if (!scrollRaf) scrollRaf = requestAnimationFrame(measure); }

    function onTouch(e) {
      var tile = e.target.closest && e.target.closest('.tile-product');
      if (!tile) return;
      var r = tile.getBoundingClientRect();
      var t = e.touches && e.touches[0];
      var x = t ? t.clientX - r.left : r.width / 2;
      var y = t ? t.clientY - r.top : r.height / 2;
      var size = Math.max(r.width, r.height) * 1.2;
      var color = PALETTE[Math.floor(Math.random() * PALETTE.length)];
      var ripple = document.createElement('div');
      ripple.className = 'ws-ripple';
      ripple.style.cssText =
        'left:' + (x - size / 2) + 'px;top:' + (y - size / 2) + 'px;' +
        'width:' + size + 'px;height:' + size + 'px;' +
        'background:' + hexToRgba(color, 0.3) + ';' +
        'border-radius:50%;transform:scale(0.3);opacity:0.9;';
      var hadStatic = getComputedStyle(tile).position === 'static';
      if (hadStatic) tile.style.position = 'relative';
      tile.appendChild(ripple);
      requestAnimationFrame(function () {
        ripple.style.transition = 'transform 500ms ease-out, opacity 600ms ease';
        ripple.style.transform = 'scale(1)';
        ripple.style.opacity = '0';
      });
      setTimeout(function () {
        if (ripple.parentNode) ripple.parentNode.removeChild(ripple);
        if (hadStatic) tile.style.position = '';
      }, 660);
    }

    container.style.display = 'block';
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('touchstart', onTouch, { passive: true });

    return function () {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('touchstart', onTouch);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      container.innerHTML = '';
      container.style.display = 'none';
      active = 0;
    };
  });

  // --- BOOTSTRAP: read flags from #ws-root data-* and enable matches ---
  function init() {
    var d = ROOT.dataset;
    if (d.cursorGraphiteTrail === 'true') enable('cursor_graphite_trail');
    if (d.scrollProgress === 'true') enable('scroll_progress');
    if (d.magneticCursor === 'true') enable('magnetic_cursor');
    if (d.typographyReveal === 'true') enable('typography_reveal');
    if (d.mobileScrollBlobs === 'true') enable('mobile_scroll_blobs');
  }

  window.wallashopDebug = {
    enable: enable,
    disable: disable,
    modules: modules,
    reduced: REDUCED,
    canHover: CAN_HOVER
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
